import { MetricModel, MetricBackendDependencies, MetricBackendError, TimezoneOffset, QueryResponse } from "./types";

/**
 * MetricQueryBackendService - TypeScript implementation
 *
 * This is a framework-independent TypeScript implementation of the metric query backend service,
 * migrated from the Angular version for better type safety and maintainability.
 */
export class MetricQueryBackendService {
  private QueryClient: any;
  private QueryGenerator: any;
  private QueryErrorInterpreter: any;
  private MetricDataAdapter: any;
  private TableDataAdapter: any;
  private DataFilter: any;
  private TableAdapter: any;
  private MetricSparkline: any;

  constructor(dependencies: MetricBackendDependencies) {
    this.QueryClient = dependencies.QueryClient;
    this.QueryGenerator = dependencies.QueryGenerator;
    this.QueryErrorInterpreter = dependencies.QueryErrorInterpreter;
    this.MetricDataAdapter = dependencies.MetricDataAdapter;
    this.TableDataAdapter = dependencies.TableDataAdapter;
    this.DataFilter = dependencies.DataFilter;
    this.TableAdapter = dependencies.TableAdapter;
    this.MetricSparkline = dependencies.MetricSparkline;
  }

  /**
   * Get metric sparkline data using the query service
   */
  async getMetricSparklineData(
    metric: MetricModel,
    timezoneOffset?: TimezoneOffset,
    tableId?: string,
    globalFilters?: any,
    scope?: any
  ): Promise<any> {
    try {
      const metricObj = metric.toJson();
      const metricConfig = metric.metricConfig();
      const filterObj = this.DataFilter.getFilterRequestBody(metricObj.filter_config);
      const { filterList } = filterObj;

      const queryObj = this.QueryGenerator.metricTimeline({
        tableId,
        filterList,
        metricConfig,
        globalFilters,
        timezoneOffset,
        scope,
      });

      return new Promise((resolve, reject) => {
        this.QueryClient.query(queryObj)
          .then((response: any) => {
            const responseData = response.data;

            const metadata = this.TableDataAdapter.formatMetadata(tableId, responseData);
            const table = this.TableAdapter.deserializeTable(tableId, metadata);
            const measureField = table.fieldByName(metricConfig.fieldName());

            const parsedTimelineData = this.MetricDataAdapter.formatTimelineData({
              fieldType: measureField ? measureField.type() : undefined,
              period: `${metricConfig.interval()}(${metricConfig.timeFieldName()})`,
              aggFn: metricConfig.func(),
              data: responseData.table,
            });

            const metricSparkline = this.MetricSparkline.fromJson(parsedTimelineData);

            resolve(metricSparkline);
          })
          .catch((response: any) => {
            const errorCode = this.QueryErrorInterpreter.errorCode(response);
            const formattedError = this.handleError("getMetricSparklineData", errorCode);
            reject(formattedError);
          });
      });
    } catch (error) {
      throw this.handleError("getMetricSparklineData", error);
    }
  }

  /**
   * Handle and format errors consistently
   */
  private handleError(operation: string, error: any): MetricBackendError {
    const errorMessage = error?.message || error?.toString() || "Unknown error occurred";

    return {
      message: `MetricQueryBackendService.${operation}: ${errorMessage}`,
      code: error?.code,
      status: error?.status,
    };
  }
}
